<template>
    <div class="schedule-center">
        <!-- next matches or results, with how many each one holds -->
        <div class="schedule-center__bar">
            <div class="schedule-center__switch" role="tablist">
                <button
                    v-for="view in views"
                    :key="view.key"
                    type="button"
                    role="tab"
                    class="schedule-center__option"
                    :class="{ 'schedule-center__option--active': active === view.key }"
                    :aria-selected="active === view.key"
                    @click="chosen = view.key"
                >
                    <v-icon small>{{ view.icon }}</v-icon>
                    {{ view.title }}
                    <span class="schedule-center__count">{{ view.matches.length }}</span>
                </button>
            </div>
        </div>

        <div v-if="!current.matches.length" class="cl-empty">{{ current.empty }}</div>

        <!-- the matches of every day under its date -->
        <section v-for="day in days" :key="day.key" class="schedule-center__day">
            <div class="schedule-center__date">
                <span class="schedule-center__weekday">{{ day.weekday }}</span>
                <span class="schedule-center__full">{{ day.date }}</span>
                <span v-if="day.today" class="schedule-center__today">Σήμερα</span>
                <span class="schedule-center__line" />
                <span class="schedule-center__n">{{ day.matches.length }} {{ day.matches.length === 1 ? 'αγώνας' : 'αγώνες' }}</span>
            </div>
            <!-- the long rows of the team page: date, the two teams with the score (or the time), the court -->
            <ol class="schedule-center__list">
                <li
                    v-for="match in day.matches"
                    :key="match.id"
                    class="schedule-center__row"
                    :class="{ 'schedule-center__row--live': isLive(match), 'schedule-center__row--postponed': match.postponed }"
                    @click="openMatch(match)"
                >
                    <span class="schedule-center__when">
                        <span v-if="!byRound" class="schedule-center__league">{{ match.cup ? 'Κύπελλο' : (match.league ? match.league.title : '') }}</span>
                        <span class="schedule-center__day-label">{{ formatWhen(match) }}</span>
                    </span>

                    <span class="schedule-center__team schedule-center__team--home" :class="{ 'schedule-center__team--lost': lost(match, 1) }" @click.stop="goToTeam(match.team1)">
                        <span class="schedule-center__name">{{ match.team1 ? match.team1.name : '' }}</span>
                        <span class="cl-logo schedule-center__logo">
                            <img v-if="match.team1 && match.team1.logo" :src="`${assetsUrl}/${match.team1.logo}`" alt="">
                        </span>
                    </span>

                    <span class="schedule-center__outcome">
                        <span v-if="isLive(match)" class="schedule-center__live"><span class="schedule-center__live-dot" />LIVE</span>
                        <span v-if="match.postponed" class="schedule-center__postponed">Αναβολή</span>
                        <span v-else-if="hasScore(match)" class="schedule-center__score">
                            <b :class="{ 'schedule-center__score--lead': !lost(match, 1) }">{{ match.scoreTeam1 }}</b>
                            <i>-</i>
                            <b :class="{ 'schedule-center__score--lead': !lost(match, 2) }">{{ match.scoreTeam2 }}</b>
                        </span>
                        <span v-else class="schedule-center__time">{{ formatTime(match.matchDatetime) }}</span>
                    </span>

                    <span class="schedule-center__team schedule-center__team--away" :class="{ 'schedule-center__team--lost': lost(match, 2) }" @click.stop="goToTeam(match.team2)">
                        <span class="cl-logo schedule-center__logo">
                            <img v-if="match.team2 && match.team2.logo" :src="`${assetsUrl}/${match.team2.logo}`" alt="">
                        </span>
                        <span class="schedule-center__name">{{ match.team2 ? match.team2.name : '' }}</span>
                    </span>

                    <span class="schedule-center__arena">
                        <template v-if="match.arena"><v-icon x-small>mdi-map-marker-outline</v-icon>{{ match.arena.name }}</template>
                    </span>

                    <span class="schedule-center__actions">
                        <button v-if="match.videoUrl && !match.pending" type="button" class="schedule-center__video" title="YouTube" @click.stop="openVideo(match)">
                            <v-icon small color="red">mdi-youtube</v-icon>
                        </button>
                        <v-icon small class="schedule-center__go">mdi-chevron-right</v-icon>
                    </span>
                </li>
            </ol>
        </section>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import { isMatchLive, refreshLiveStatuses, withLiveData, pollWhileVisible, LIVE_TICK_MS } from '@/utils/matchLive'

    const TIMEZONE = 'Europe/Athens'

    function time(match) {
        return match.matchDatetime ? momentTimezone(match.matchDatetime).valueOf() : 0
    }

    export default {
        props: {
            matches: {
                type: Array,
                default: null
            }
        },
        data() {
            return {
                // null: the page picks (the next matches, the results when there are none)
                chosen: null,
                assetsUrl: 'https://storage.googleapis.com',
                now: Date.now(),
                stopTick: null
            }
        },
        computed: {
            // the live score and the end of a game from the stat logs, ahead of the next reload
            liveMatches() {
                return (this.matches || []).map(match => withLiveData(match, this.now))
            },
            upcoming() {
                // by date, the postponed ones last
                return this.liveMatches
                    .filter(match => match.pending)
                    .sort((a, b) => (Boolean(a.postponed) - Boolean(b.postponed)) || (time(a) - time(b)))
            },
            results() {
                return this.liveMatches
                    .filter(match => !match.pending)
                    .sort((a, b) => time(b) - time(a))
            },
            views() {
                return [
                    { key: 'upcoming', title: 'Επόμενοι αγώνες', icon: 'mdi-calendar-clock-outline', matches: this.upcoming, empty: 'Δεν υπάρχουν προγραμματισμένοι αγώνες με αυτά τα φίλτρα.' },
                    { key: 'results', title: 'Αποτελέσματα', icon: 'mdi-scoreboard-outline', matches: this.results, empty: 'Δεν υπάρχουν αποτελέσματα με αυτά τα φίλτρα.' }
                ]
            },
            active() {
                if (this.chosen) return this.chosen
                return !this.upcoming.length && this.results.length ? 'results' : 'upcoming'
            },
            current() {
                return this.views.find(view => view.key === this.active)
            },
            // one league: by matchday; several leagues: by day
            byRound() {
                const matches = this.current.matches
                const leagues = new Set(matches.map(match => (match.league ? match.league.id : null)))
                return matches.length > 0 && leagues.size === 1 && !leagues.has(null) && matches.every(match => match.round || match.customRoundText)
            },
            days() {
                return this.byRound ? this.rounds() : this.dates()
            }
        },
        watch: {
            // new filters: the page picks again
            matches: {
                handler() {
                    this.chosen = null
                    refreshLiveStatuses(this.matches || [])
                },
                immediate: true
            }
        },
        created() {
            this.stopTick = pollWhileVisible(() => {
                this.now = Date.now()
                refreshLiveStatuses(this.matches || [], this.now)
            }, LIVE_TICK_MS)
        },
        beforeDestroy() {
            this.stopTick()
        },
        methods: {
            isLive(match) {
                return isMatchLive(match, this.now)
            },
            hasScore(match) {
                return this.isLive(match) || (!match.pending && !(match.scoreTeam1 === 0 && match.scoreTeam2 === 0))
            },
            // the losing side of a played match, dimmed
            lost(match, side) {
                if (match.pending || this.isLive(match) || !this.hasScore(match)) return false
                const own = side === 1 ? match.scoreTeam1 : match.scoreTeam2
                const other = side === 1 ? match.scoreTeam2 : match.scoreTeam1
                return own < other
            },
            // the day, and the hour of a played match (the next ones show it in the middle)
            formatWhen(match) {
                const day = this.formatDate(match.matchDatetime)
                return match.matchDatetime && this.hasScore(match) ? `${day} · ${this.formatTime(match.matchDatetime)}` : day
            },
            formatDate(date) {
                return date ? momentTimezone(date).tz(TIMEZONE).locale('el').format('ddd D MMM') : '–'
            },
            formatTime(date) {
                return date ? momentTimezone(date).tz(TIMEZONE).format('HH:mm') : '–:–'
            },
            openMatch(match) {
                this.$router.push({ name: 'Match', params: { id: match.id } })
            },
            goToTeam(team) {
                if (team && team.id) this.$router.push({ name: 'Team', params: { id: team.id } })
            },
            openVideo(match) {
                window.open(match.videoUrl.replace('embed/', 'watch?v='), '_blank', 'noopener')
            },
            rounds() {
                const today = momentTimezone().tz(TIMEZONE).format('YYYY-MM-DD')
                const rounds = []
                this.current.matches.forEach(match => {
                    const key = match.customRoundText || `round-${match.round}`
                    let group = rounds.find(item => item.key === key)
                    if (!group) {
                        // a named round (e.g. a final) comes after the numbered ones
                        const order = match.customRoundText ? 10000 + rounds.length : Number(match.round)
                        group = { key, order, weekday: match.customRoundText || `Αγωνιστική ${match.round}`, date: '', today: false, matches: [] }
                        rounds.push(group)
                    }
                    group.matches.push(match)
                })
                // the next matchdays first to last, the results last to first
                const direction = this.active === 'results' ? -1 : 1
                rounds.sort((a, b) => direction * (a.order - b.order))
                // the days of every matchday, e.g. "19 – 27 Μαΐου 2026"
                rounds.forEach(group => {
                    const days = group.matches.filter(match => match.matchDatetime && !match.postponed)
                        .map(match => momentTimezone(match.matchDatetime).tz(TIMEZONE).locale('el'))
                        .sort((a, b) => a.valueOf() - b.valueOf())
                    if (days.length) {
                        const first = days[0]
                        const last = days[days.length - 1]
                        group.date = first.isSame(last, 'day')
                            ? first.format('dddd D MMMM YYYY')
                            : `${first.format(first.isSame(last, 'month') ? 'D' : 'D MMMM')} – ${last.format('D MMMM YYYY')}`
                        group.today = days.some(day => day.format('YYYY-MM-DD') === today)
                    }
                })
                return rounds
            },
            dates() {
                const today = momentTimezone().tz(TIMEZONE).format('YYYY-MM-DD')
                const days = []
                this.current.matches.forEach(match => {
                    // the postponed ones without a new date go together at the end
                    const day = match.postponed || !match.matchDatetime ? null : momentTimezone(match.matchDatetime).tz(TIMEZONE).locale('el')
                    const key = day ? day.format('YYYY-MM-DD') : 'postponed'
                    let group = days.find(item => item.key === key)
                    if (!group) {
                        group = {
                            key,
                            weekday: day ? day.format('dddd') : 'Αναβλήθηκαν',
                            date: day ? day.format('D MMMM YYYY') : 'Χωρίς νέα ημερομηνία',
                            today: key === today,
                            matches: []
                        }
                        days.push(group)
                    }
                    group.matches.push(match)
                })
                return days
            }
        }
    }
</script>

<style scoped>
.schedule-center__bar {
    display: flex;
    margin-bottom: 18px;
}

.schedule-center__switch {
    display: inline-flex;
    max-width: 100%;
    padding: 4px;
    border-radius: 999px;
    background: #232323;
    box-shadow: 0 6px 18px rgba(35, 35, 35, 0.14);
    overflow-x: auto;
    scrollbar-width: none;
}

.schedule-center__option {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.7);
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.1rem;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    transition: color 0.15s, background 0.15s;
}

.schedule-center__option .v-icon {
    color: #03e5b7 !important;
}

.schedule-center__option:hover {
    color: #fff;
}

.schedule-center__option--active {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #fff;
}

.schedule-center__option--active .v-icon {
    color: #fff !important;
}

.schedule-center__count {
    padding: 0 7px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0;
}

.schedule-center__day + .schedule-center__day {
    margin-top: 26px;
}

.schedule-center__date {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 12px;
    font-family: Roboto, Arial, sans-serif;
}

.schedule-center__weekday {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.45rem;
    line-height: 1;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #232323;
}

.schedule-center__full {
    color: rgba(35, 35, 35, 0.65);
    font-size: 0.9rem;
    font-weight: 600;
}

.schedule-center__today {
    align-self: center;
    padding: 2px 9px;
    border-radius: 999px;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #fff;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.schedule-center__line {
    flex: 1 1 auto;
    align-self: center;
    height: 1px;
    background: rgba(35, 35, 35, 0.12);
}

.schedule-center__n {
    color: rgba(35, 35, 35, 0.5);
    font-size: 0.78rem;
    font-weight: 600;
    white-space: nowrap;
}

/* ---------- the rows, as on the team page ---------- */
.schedule-center__list {
    margin: 0;
    padding: 0;
    list-style: none;
    border-radius: 12px;
    background: #232323;
    box-shadow: 0 6px 20px rgba(35, 35, 35, 0.12);
    overflow: hidden;
    font-family: Roboto, Arial, sans-serif;
}

.schedule-center__row {
    display: grid;
    grid-template-columns: 130px minmax(0, 1fr) 120px minmax(0, 1fr) minmax(0, 190px) 56px;
    align-items: center;
    gap: 14px;
    padding: 11px 16px;
    color: #fff;
    cursor: pointer;
    transition: background 0.15s;
}

.schedule-center__row + .schedule-center__row {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.schedule-center__row:hover {
    background: rgba(3, 229, 183, 0.06);
}

.schedule-center__row--live {
    background: rgba(239, 68, 68, 0.1);
    box-shadow: inset 3px 0 0 #ef4444;
}

.schedule-center__row--postponed {
    opacity: 0.7;
}

.schedule-center__when {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.schedule-center__league {
    overflow: hidden;
    color: #03e5b7;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.schedule-center__day-label {
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.6);
    text-transform: capitalize;
}

.schedule-center__team {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.schedule-center__team--home {
    justify-content: flex-end;
    text-align: right;
}

.schedule-center__team--lost {
    color: rgba(255, 255, 255, 0.55);
}

.schedule-center__logo {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
}

.schedule-center__name {
    overflow: hidden;
    font-weight: 700;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.schedule-center__team:hover .schedule-center__name {
    color: #03e5b7;
}

.schedule-center__outcome {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
}

.schedule-center__score {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.6rem;
    line-height: 1;
    white-space: nowrap;
}

.schedule-center__score b {
    font-weight: 400;
    color: rgba(255, 255, 255, 0.55);
}

.schedule-center__score b.schedule-center__score--lead {
    color: #fff;
}

.schedule-center__score i {
    font-style: normal;
    color: rgba(255, 255, 255, 0.35);
}

.schedule-center__time {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.6rem;
    line-height: 1;
    color: #03e5b7;
}

.schedule-center__live,
.schedule-center__postponed {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.08em;
}

.schedule-center__live {
    background: #ef4444;
}

.schedule-center__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: schedule-center-live 1.2s ease-in-out infinite;
}

@keyframes schedule-center-live {
    50% {
        opacity: 0.25;
    }
}

.schedule-center__postponed {
    background: #f44336;
}

.schedule-center__arena {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    min-width: 0;
    overflow: hidden;
    font-size: 0.74rem;
    color: rgba(255, 255, 255, 0.5);
    white-space: nowrap;
    text-overflow: ellipsis;
}

.schedule-center__arena .v-icon {
    color: inherit !important;
}

.schedule-center__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
}

.schedule-center__video {
    display: inline-flex;
    padding: 4px;
    border-radius: 6px;
}

.schedule-center__video:hover {
    background: rgba(255, 255, 255, 0.08);
}

.schedule-center__go {
    color: rgba(255, 255, 255, 0.35) !important;
}

.schedule-center__row:hover .schedule-center__go {
    color: #03e5b7 !important;
}

/* medium screens: no court column */
@media (max-width: 1100px) {
    .schedule-center__row {
        grid-template-columns: 110px minmax(0, 1fr) 110px minmax(0, 1fr) 40px;
    }

    .schedule-center__arena {
        display: none;
    }
}

/* phones: league and date above, the two teams and the score on one line */
@media (max-width: 700px) {
    .schedule-center__row {
        grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
        grid-template-areas:
            'when when when'
            'home outcome away';
        gap: 8px 10px;
        padding: 10px 12px;
    }

    .schedule-center__when {
        grid-area: when;
        flex-direction: row;
        align-items: baseline;
        gap: 8px;
    }

    .schedule-center__team--home {
        grid-area: home;
    }

    .schedule-center__team--away {
        grid-area: away;
    }

    .schedule-center__outcome {
        grid-area: outcome;
    }

    .schedule-center__logo {
        width: 30px;
        height: 30px;
    }

    .schedule-center__name {
        font-size: 0.82rem;
    }

    .schedule-center__score,
    .schedule-center__time {
        font-size: 1.35rem;
    }

    .schedule-center__actions {
        display: none;
    }
}

@media (max-width: 700px) {
    .schedule-center__option {
        padding: 7px 12px;
        font-size: 1rem;
    }

    .schedule-center__weekday {
        font-size: 1.2rem;
        white-space: nowrap;
    }

    .schedule-center__date {
        flex-wrap: wrap;
        gap: 4px 8px;
    }

    .schedule-center__line {
        display: none;
    }

    .schedule-center__n {
        margin-left: auto;
    }

    .schedule-center__full {
        font-size: 0.8rem;
    }
}
</style>
